import dayjs from 'dayjs';
import type { flightEntity } from 'freedom-shared';

import { dateUtils } from '../../shared/lib';

const NIGHT_START_HOUR = 22;
const NIGHT_END_HOUR = 6;

const isNightHour = (dateTime: string) => {
  const hour = dayjs(dateUtils.removeTimezone(dateTime)).hour();
  return hour < NIGHT_END_HOUR || hour >= NIGHT_START_HOUR;
};

/**
 * A connection is considered a night one when the layover spans different
 * calendar days or either the arrival or the next departure falls into night
 * hours (22:00–06:00). Kept in sync with the `byNightTransfer` search filter so
 * the "Ночная пересадка" row appears exactly for offers that filter excludes.
 */
export const isNightTimeTransfer = (arrivalAt: string, departureAt: string) => {
  const arrival = dayjs(dateUtils.removeTimezone(arrivalAt));
  const departure = dayjs(dateUtils.removeTimezone(departureAt));
  const isDiffDay = arrival.day() !== departure.day();
  const isNightTime = isNightHour(arrivalAt) || isNightHour(departureAt);
  return isDiffDay || isNightTime;
};

const connectionDuration = (segment: flightEntity.types.MappedSegment) => {
  const durationInSeconds = segment.connection?.duration;
  if (!durationInSeconds) {
    return null;
  }

  return dateUtils.prettyDurationFromSeconds(durationInSeconds);
};

const isSelfTransfer = (segment: flightEntity.types.MappedSegment) => {
  const guarantor = segment.connection?.guarantor;
  return guarantor === null || guarantor === 'tenant';
};

const hasNextDayDep = (segment: flightEntity.types.MappedSegment) => {
  const [hours, minutes] = segment.arr.time.split(':');
  const duration = segment.connection?.duration;
  const durationInHours = !duration ? 0 : duration / 3600;
  const nextDepHours =
    parseInt(hours) + parseInt(minutes) / 60 + durationInHours;

  return nextDepHours > 24;
};

export const isNightConnection = (
  segment: flightEntity.types.MappedSegment,
  nextSegment: flightEntity.types.MappedSegment,
) => isNightTimeTransfer(segment.arr.at, nextSegment.dep.at);

export const flightConnectionLib = {
  connectionDuration,
  isSelfTransfer,
  hasNextDayDep,
  isNightTimeTransfer,
  isNightConnection,
};
